import moment from "moment";
import React from "react";
import { classNames, isValidEmail } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { Card, FcDrawer, FieldError, FieldLabel, Icon, inputClass } from "../schedule/ui";
import { apiResult, COUNTRY_CODES } from "./memberUtils";

/**
 * Add / edit a member (new design). Same calls as the old AddNewMemberForm:
 *  add  → POST bookings/addMembers {username, countryCode, phoneNumber, fullName, email, gender, dob, actionType}
 *  edit → POST bookings/updateMember {consumerId, fullName, email, gender, dob}
 * `member` = a getMyClubMembers row ({consumer_detail}) when editing, null when adding.
 */

const GENDERS = ["Female", "Male", "Other"];

export default function MemberFormDrawer({ open, member, onClose, onSaved = () => null }) {
  const { addSuccessToast } = useToast();
  const isEdit = !!member;
  const [f, setF] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const c = member?.consumer_detail || {};
    setF({
      fullName: c.fullName || "",
      countryCode: c.countryCode || "+31",
      phone: c.phoneNumber || "",
      email: c.email || "",
      gender: c.gender || "",
      dob: c.dob ? moment(c.dob).format("YYYY-MM-DD") : "",
      trial: true,
    });
    setErrors({});
    setSaving(false);
  }, [open, member]);

  const set = (k, v) => {
    setF((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "", form: "" }));
  };

  const save = async () => {
    const e = {};
    if (!f.fullName?.trim()) e.fullName = "Enter their full name.";
    const digits = String(f.phone || "").replace(/\D/g, "").replace(/^0+/, "");
    if (!isEdit && !digits) e.phone = "Enter their mobile number.";
    else if (!isEdit && (digits.length < 8 || digits.length > 10)) e.phone = "Enter a valid mobile number (8 to 10 digits, without the country code).";
    if (!f.email?.trim()) e.email = "Enter their email address.";
    else if (!isValidEmail(f.email.trim())) e.email = "This email address doesn't look right.";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const dob = f.dob || (isEdit ? "" : moment().subtract(20, "years").format("YYYY-MM-DD"));
      const res = await axiosInstance(
        isEdit
          ? {
              method: "post",
              url: `${getUrlForBookings()}updateMember`,
              data: { consumerId: member.consumer_detail?.consumerId, fullName: f.fullName.trim(), email: f.email.trim(), gender: f.gender, dob },
            }
          : {
              method: "post",
              url: `${getUrlForBookings()}addMembers`,
              data: {
                username: `${f.countryCode}${digits}`,
                countryCode: f.countryCode,
                phoneNumber: digits,
                fullName: f.fullName.trim(),
                email: f.email.trim(),
                gender: f.gender,
                dob,
                actionType: f.trial ? "free_trial_credit" : "no_credits",
              },
            }
      );
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(isEdit ? "Changes saved" : `${f.fullName.trim()} added${f.trial ? " with 1 free trial credit" : ""}`);
        onSaved();
        onClose();
      } else {
        setErrors({ form: msg || "Couldn't save this member." });
      }
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FcDrawer
      open={open}
      onClose={onClose}
      eyebrow={isEdit ? "EDIT MEMBER" : "ADD MEMBER"}
      title={isEdit ? member?.consumer_detail?.fullName || "Edit member" : "Add a member"}
      subtitle={isEdit ? "Update their contact details." : "They get a FitClub account and a welcome email."}
      primaryLabel={isEdit ? "Save changes" : "Add member"}
      onPrimary={save}
      busy={saving}
    >
      {errors.form && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{errors.form}</div>}
      <Card title="Contact">
        <div className="flex flex-col gap-4">
          <div>
            <FieldLabel htmlFor="mf-name">Full name</FieldLabel>
            <input id="mf-name" value={f.fullName || ""} onChange={(e) => set("fullName", e.target.value)} placeholder="e.g. Emma de Jong" className={inputClass(errors.fullName)} />
            <FieldError>{errors.fullName}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="mf-phone">Mobile number</FieldLabel>
            <div className="flex gap-2">
              <select aria-label="Country code" disabled={isEdit} value={f.countryCode || "+31"} onChange={(e) => set("countryCode", e.target.value)} className={classNames(inputClass(false), "!w-28 shrink-0 px-3 disabled:bg-[#F7F4F8] disabled:text-gray-500")}>
                {COUNTRY_CODES.concat(COUNTRY_CODES.includes(f.countryCode) || !f.countryCode ? [] : [f.countryCode]).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input id="mf-phone" inputMode="tel" disabled={isEdit} value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="6 1234 5678" className={classNames(inputClass(errors.phone), "min-w-0 flex-1 disabled:bg-[#F7F4F8] disabled:text-gray-500")} />
            </div>
            {isEdit && <p className="mt-1.5 text-xs text-gray-500">The mobile number is their login, so it can't be changed here.</p>}
            <FieldError>{errors.phone}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="mf-email">Email</FieldLabel>
            <input id="mf-email" type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="name@email.com" className={inputClass(errors.email)} />
            <FieldError>{errors.email}</FieldError>
          </div>
        </div>
      </Card>
      <Card title={<span>About them <span className="ml-1 text-xs font-semibold text-gray-400">Optional</span></span>}>
        <FieldLabel>Gender</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {GENDERS.map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={f.gender === g}
              onClick={() => set("gender", f.gender === g ? "" : g)}
              className={classNames("h-10 rounded-full border px-4 text-sm font-extrabold", f.gender === g ? "border-indigo-bz bg-[#F3E9FF] text-[#6A00BA]" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="mt-4 sm:max-w-[240px]">
          <FieldLabel htmlFor="mf-dob">Date of birth</FieldLabel>
          <input id="mf-dob" type="date" max={moment().format("YYYY-MM-DD")} value={f.dob || ""} onChange={(e) => set("dob", e.target.value)} className={inputClass(false)} />
        </div>
      </Card>
      {!isEdit && (
        <section className="flex items-center gap-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
            <Icon className="h-5 w-5" d={<><rect x="3" y="8" width="18" height="13" rx="2" /><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1.5S9 8 12 8c3 0 6-1 6-1.5S14 4 12 8z" /></>} />
          </span>
          <div className="flex-1">
            <div className="text-[15px] font-extrabold text-gray-900">Give 1 free trial credit</div>
            <div className="mt-0.5 text-[13px] text-gray-500">They can book any one class for free.</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!f.trial}
            aria-label="Give 1 free trial credit"
            onClick={() => set("trial", !f.trial)}
            className={classNames("flex h-[30px] w-[52px] shrink-0 rounded-full p-[3px] transition-colors", f.trial ? "justify-end bg-indigo-bz" : "justify-start bg-[#D9D2DC]")}
          >
            <span className="h-6 w-6 rounded-full bg-white shadow" />
          </button>
        </section>
      )}
    </FcDrawer>
  );
}
